import { chipColorForKey, entityColorKey } from "@/analytics/core/chart-palette";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { useEffect, useMemo, useState, type MouseEvent } from "react";

export interface MultiComboboxOption {
  id: number;
  name: string;
  colorKey?: string;
}

interface MultiComboboxProps {
  options: MultiComboboxOption[];
  selectedIds: number[];
  onApply: (ids: number[]) => void;
  allLabel: string;
  /** Prefix for stable entity colors (e.g. "team" → `team-3` matches chart series keys). */
  colorKeyPrefix?: "team" | "rep";
  /** Sorted-index palette map (teams); must match chart `seriesColorForKey`. */
  entityColorIndexMap?: ReadonlyMap<string, number>;
  searchPlaceholder?: string;
  className?: string;
}

export function MultiCombobox({
  options,
  selectedIds,
  onApply,
  allLabel,
  colorKeyPrefix,
  entityColorIndexMap,
  searchPlaceholder = "Search…",
  className,
}: MultiComboboxProps) {
  const [open, setOpen] = useState(false);
  const [draftIds, setDraftIds] = useState<number[]>(selectedIds);

  useEffect(() => {
    if (open) setDraftIds(selectedIds);
  }, [open, selectedIds]);

  const appliedOptions = useMemo(
    () => options.filter((option) => selectedIds.includes(option.id)),
    [options, selectedIds],
  );

  const draftChanged =
    draftIds.length !== selectedIds.length || draftIds.some((id) => !selectedIds.includes(id));

  const toggleDraft = (id: number) => {
    setDraftIds((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    );
  };

  const apply = () => {
    onApply(draftIds);
    setOpen(false);
  };

  const cancel = () => {
    setDraftIds(selectedIds);
    setOpen(false);
  };

  const removeApplied = (id: number, event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onApply(selectedIds.filter((value) => value !== id));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "inline-flex min-h-9 min-w-[10rem] max-w-md items-center justify-between gap-2 rounded-lg",
            "border border-[var(--color-border)] bg-white px-2 py-1.5 text-sm",
            "hover:bg-[#F8FAFC] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B8CDE8]/60",
            className,
          )}
        >
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
            {appliedOptions.length === 0 ? (
              <span className="px-1 font-medium text-[#64748B]">{allLabel}</span>
            ) : (
              appliedOptions.map((option) => {
                const colorKey =
                  option.colorKey ??
                  (colorKeyPrefix ? entityColorKey(colorKeyPrefix, option.id) : String(option.id));
                // Prefer stable map (same slots as chart/pie) when provided.
                const chip = chipColorForKey(colorKey, entityColorIndexMap);
                return (
                  <span
                    key={option.id}
                    className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium"
                    style={{
                      backgroundColor: chip.bg,
                      color: chip.text,
                      borderColor: chip.border,
                    }}
                  >
                    {option.name}
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(event) => removeApplied(option.id, event)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          removeApplied(option.id, event as unknown as MouseEvent);
                        }
                      }}
                      className="rounded-full opacity-60 hover:opacity-100"
                      aria-label={`Remove ${option.name}`}
                    >
                      <X className="h-3 w-3" />
                    </span>
                  </span>
                );
              })
            )}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-[var(--color-text-muted)]" />
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-72 p-0" align="start">
        <Command shouldFilter>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>No results.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value={allLabel}
                onSelect={() => setDraftIds([])}
                className="justify-between"
              >
                <span>{allLabel}</span>
                {draftIds.length === 0 ? <Check className="h-4 w-4" /> : null}
              </CommandItem>
              {options.map((option) => {
                const selected = draftIds.includes(option.id);
                return (
                  <CommandItem
                    key={option.id}
                    value={option.name}
                    onSelect={() => toggleDraft(option.id)}
                    className="justify-between"
                  >
                    <span>{option.name}</span>
                    {selected ? <Check className="h-4 w-4" /> : null}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>

        <div className="flex items-center justify-end gap-2 border-t border-[var(--color-border)] px-3 py-2">
          <Button variant="ghost" size="sm" onClick={cancel}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={apply} disabled={!draftChanged}>
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
